import React, { useState } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ChevronRight, ChevronDown, Search, ChevronsUpDown, Download, Sparkles, Copy } from 'lucide-react';
import { dashboardAPI } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const monthNamesShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function statusBadgeClass(status) {
  if (status === 'Eingereicht') return 'bg-green-100 text-green-700';
  if (status === 'In Bearbeitung') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-500';
}

function diffClass(val) {
  if (val < 0) return 'text-right text-red-600';
  if (val > 0) return 'text-right text-green-600';
  return 'text-right';
}

function diffPrefix(val) {
  return val > 0 ? '+' : '';
}

function MonthRow({ m }) {
  const name = monthNamesShort[m.month - 1];
  const cls = diffClass(m.differenz);
  const prefix = diffPrefix(m.differenz);
  const badgeCls = statusBadgeClass(m.status);
  const sick = m.kranktage_kind || '-';

  return (
    <TableRow>
      <TableCell className="font-medium">{name}</TableCell>
      <TableCell className="text-right">{m.soll}</TableCell>
      <TableCell className="text-right">{m.ist}</TableCell>
      <TableCell className={cls}>{prefix}{m.differenz}</TableCell>
      <TableCell className="text-center">{sick}</TableCell>
      <TableCell><Badge className={badgeCls}>{m.status}</Badge></TableCell>
    </TableRow>
  );
}

function TotalsRow({ totals }) {
  const cls = diffClass(totals.differenz);
  const prefix = diffPrefix(totals.differenz);
  const sick = totals.kranktage_kind || '-';

  return (
    <TableRow className="bg-slate-50 font-semibold">
      <TableCell>Jahressumme</TableCell>
      <TableCell className="text-right">{totals.soll}</TableCell>
      <TableCell className="text-right">{totals.ist}</TableCell>
      <TableCell className={cls}>{prefix}{totals.differenz}</TableCell>
      <TableCell className="text-center">{sick}</TableCell>
      <TableCell></TableCell>
    </TableRow>
  );
}

function YearlyCard({ row, idx, open, onToggle, year }) {
  const title = row.employee_name + ' – ' + row.child_name;
  const school = '(' + row.school_name + ')';
  const t = row.year_totals;
  const summary = t.ist + ' / ' + t.soll + ' Std.';
  const dCls = t.differenz < 0 ? 'text-red-600' : t.differenz > 0 ? 'text-green-600' : 'text-slate-500';
  const [aiReport, setAiReport] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDialog, setAiDialog] = useState(false);

  function downloadPDF(e) {
    e.stopPropagation();
    dashboardAPI.getYearlyOverviewPDF(year, row.assignment_id).then(function(res) {
      var link = document.createElement('a');
      link.href = 'data:application/pdf;base64,' + res.data.pdf;
      link.download = res.data.filename;
      link.click();
      toast.success('PDF heruntergeladen');
    }).catch(function() { toast.error('Fehler beim PDF-Download'); });
  }

  function generateAIReport(e) {
    e.stopPropagation();
    setAiLoading(true); setAiReport(null); setAiDialog(true);
    dashboardAPI.getYearlyAIReport(year, row.assignment_id).then(function(res) {
      setAiReport(res.data.report);
    }).catch(function(err) {
      var msg = fehlertext(err, 'Fehler');
      if (err.response && err.response.status === 400) {
        setAiReport('Bitte zuerst in den Einstellungen unter „KI-Funktionen" einen API-Schlüssel hinterlegen.');
      } else {
        toast.error(msg);
        setAiDialog(false);
      }
    }).finally(function() { setAiLoading(false); });
  }

  function copyReport() {
    if (aiReport) {
      navigator.clipboard.writeText(aiReport).then(function() { toast.success('Text kopiert'); });
    }
  }

  let content = null;
  if (open) {
    const monthElements = [];
    for (let i = 0; i < row.months.length; i++) {
      monthElements.push(<MonthRow key={i} m={row.months[i]} />);
    }
    content = (
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Monat</TableHead>
                <TableHead className="text-right">Soll</TableHead>
                <TableHead className="text-right">Ist</TableHead>
                <TableHead className="text-right">Differenz</TableHead>
                <TableHead className="text-center">Krankheitstage Kind</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthElements}
              <TotalsRow totals={row.year_totals} />
            </TableBody>
          </Table>
        </div>
      </CardContent>
    );
  }

  return (
    <Card data-testid={'yearly-card-' + idx}>
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-50/50 transition-colors py-3"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            {open
              ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
              : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
            }
            <CardTitle className="text-base truncate">
              {title} <span className="text-slate-400 font-normal">{school}</span>
            </CardTitle>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 text-sm">
            <span className="text-slate-500 font-mono">{summary}</span>
            <span className={'font-mono font-medium ' + dCls}>{diffPrefix(t.differenz)}{t.differenz}</span>
            <button onClick={downloadPDF} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="PDF herunterladen">
              <Download className="h-4 w-4" />
            </button>
            <button onClick={generateAIReport} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600" title="KI-Bericht erstellen">
              <Sparkles className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      {content}
      <Dialog open={aiDialog} onOpenChange={setAiDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> KI-Jahresbericht</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{title} {school} – {year}</p>
            {aiLoading && <div className="flex items-center gap-2 text-slate-500 py-4"><span className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-violet-600 rounded-full"></span> Bericht wird erstellt…</div>}
            {aiReport && (
              <div className="space-y-2">
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{aiReport}</div>
                <Button variant="outline" size="sm" onClick={copyReport}><Copy className="h-4 w-4 mr-1" /> Text kopieren</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function YearlyOverviewCards({ rows, year }) {
  const [query, setQuery] = useState('');
  const [openSet, setOpenSet] = useState({});

  function downloadAllPDF() {
    dashboardAPI.getYearlyOverviewPDF(year).then(function(res) {
      var link = document.createElement('a');
      link.href = 'data:application/pdf;base64,' + res.data.pdf;
      link.download = res.data.filename;
      link.click();
      toast.success('Gesamt-PDF heruntergeladen');
    }).catch(function() { toast.error('Fehler beim PDF-Download'); });
  }

  const q = query.toLowerCase();
  const filtered = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (q && !(r.employee_name + ' ' + r.child_name + ' ' + r.school_name).toLowerCase().includes(q)) {
      continue;
    }
    filtered.push({ row: r, idx: i });
  }

  function toggleCard(idx) {
    setOpenSet(function(prev) {
      const next = Object.assign({}, prev);
      if (next[idx]) { delete next[idx]; } else { next[idx] = true; }
      return next;
    });
  }

  function expandAll() {
    const next = {};
    for (let i = 0; i < filtered.length; i++) { next[filtered[i].idx] = true; }
    setOpenSet(next);
  }

  function collapseAll() {
    setOpenSet({});
  }

  const cards = [];
  for (let i = 0; i < filtered.length; i++) {
    const f = filtered[i];
    cards.push(
      <YearlyCard
        key={f.idx}
        row={f.row}
        idx={f.idx}
        open={!!openSet[f.idx]}
        onToggle={function() { toggleCard(f.idx); }}
        year={year}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Mitarbeiter, Kind oder Schule suchen..."
            value={query}
            onChange={function(e) { setQuery(e.target.value); }}
            className="pl-9"
            data-testid="yearly-search"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={expandAll} data-testid="expand-all-btn">
          <ChevronsUpDown className="h-4 w-4 mr-1" /> Alle aufklappen
        </Button>
        <Button variant="ghost" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">
          <ChevronsUpDown className="h-4 w-4 mr-1" /> Alle zuklappen
        </Button>
        <span className="text-sm text-slate-400">{filtered.length} Einsätze</span>
        <Button variant="outline" size="sm" onClick={downloadAllPDF} data-testid="yearly-pdf-all">
          <Download className="h-4 w-4 mr-1" /> Gesamt-PDF
        </Button>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-slate-500">Keine Treffer</div>
      )}

      <div className="space-y-3">{cards}</div>
    </div>
  );
}
